import { Address, EMPTY_ADDRESS } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AdressFieldDenormalized,
  DateFieldDenormalized,
  EmailFieldDenormalized,
  EnrolleeInfo,
  EnrollmentFormDenormalized,
  EnrollmentFormFieldDenormalized,
  FieldType,
  FormFieldSubmission,
  FormFieldSubmissionAddress,
  FormFieldSubmissionDate,
  FormFieldSubmissionEmail,
  FormFieldSubmissionNumber,
  FormFieldSubmissionText,
  FormFieldSubmissionUserRelationship,
  IEnrollmentFormSubmission,
  NumberFieldDenormalizd,
  SubmissionResponseStatus,
  SubmitFormResponse,
  TextFieldDenormalized,
  TextRestriction,
  UserRelationFieldDenormalized
} from '@dadohr/shared/domainTypes/enrollmentForms';
import DateFnsUtils from '@date-io/moment';
import styled from '@emotion/styled';
import { Button, IconButton, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import { validate as isValidEmail } from 'email-validator';
import { keyBy } from 'lodash';
import { X } from 'lucide-react';
import moment from 'moment-timezone';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { AddressForm } from '../../../../../components/AddessForm';
import { ButtonLoader } from '../../../../../components/ButtonWithPromise';
import { Form } from '../../../../../components/Form';
import { FormField } from '../../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../../components/Form/FormSubmitButton';
import { ImageContainer } from '../../../../../components/ImageContainer';
import { InfoBox } from '../../../../../components/InfoBox';
import { Loader } from '../../../../../components/Loader';
import { MailTo } from '../../../../../components/MailTo';
import { MarkdownText } from '../../../../../components/MarkdownText';
import { NotFound } from '../../../../../components/NotFound';
import { OrgLogo } from '../../../../../components/OrgLogo';
import { SomethingWentWrong } from '../../../../../components/SomethingWentWrong';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer, FlexItem } from '../../../../../layout/Flex';
import { Page } from '../../../../../layout/Page';
import { toMomentFromAny } from '../../../../../services/time';
import {
  useStandaloneAppContext,
  useStandaloneAppThemeOverride
} from '../../../../hooks/useStandaloneAppContext';
import { WrongSsoUserInSession } from '../../components/WrongSsoUserInSession';
import {
  NOT_FOUND_MESSAGES,
  submitEnrollmentForm,
  useEnrollmentForm as useEnrollmentFormRenderInfo
} from '../../service';
import { MaybeImpersonationBanner } from '../../../../components/ImpersonationBanner';

const simpleHash = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }
  return new Uint32Array([hash])[0].toString(36);
};

const TextFormField = ({
  formField,
  isReadOnly
}: {
  formField: TextFieldDenormalized;
  isReadOnly: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FormField<FormFieldSubmissionText>
      validate={(input) => {
        return input && input.value ? undefined : t('Please provide a value');
      }}
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => {
        if (!formField.validationParameters.restrictAnswerChoice) {
          return (
            <TextField
              fullWidth
              variant="outlined"
              label={formField.renderParameters.displayName}
              error={meta.dirty && meta.error}
              helperText={meta.dirty && meta.error ? meta.error : ''}
              required
              value={input.value.value}
              disabled={isReadOnly}
              onChange={(ev) => {
                const newInput: FormFieldSubmissionText = {
                  id: formField.id,
                  type: FieldType.TEXT,
                  value: ev.target.value
                };
                input.onChange(newInput);
              }}
            />
          );
        } else {
          const options =
            formField.validationParameters.restrictionType ===
            TextRestriction.EXISTING
              ? formField.renderParameters.existingValues || []
              : formField.validationParameters.allowedValuesList || [];
          return (
            <Autocomplete
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={meta.dirty && meta.error}
                  helperText={meta.dirty && meta.error ? meta.error : ''}
                  label={formField.renderParameters.displayName}
                />
              )}
              disabled={isReadOnly}
              value={input.value.value}
              onInputChange={(e, v) => {
                // This is necessarz to make the field required
                // and make the normal form mechanism enforce it
                if (!v) {
                  input.onChange(null);
                }
              }}
              onChange={(e, v) => {
                if (v) {
                  const newInput: FormFieldSubmissionText = {
                    id: formField.id,
                    type: FieldType.TEXT,
                    value: v
                  };
                  input.onChange(newInput);
                }
              }}
              options={options}
            />
          );
        }
      }}
    </FormField>
  );
};

const NumberFormField = ({
  formField,
  isReadOnly
}: {
  formField: NumberFieldDenormalizd;
  isReadOnly: boolean;
}) => {
  return (
    <FormField<FormFieldSubmissionNumber>
      validate={(input) => {
        if (!formField.validationParameters.restrictToInterval || !input) {
          return undefined;
        }
        if (
          formField.validationParameters.max !== null &&
          input.value > formField.validationParameters.max
        ) {
          return `The value should be less than or equal to  ${formField.validationParameters.max}`;
        }
        if (
          formField.validationParameters.min !== null &&
          input.value < formField.validationParameters.min
        ) {
          return `The value should be greater than or equal to  ${formField.validationParameters.min}`;
        }
      }}
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => (
        <TextField
          fullWidth
          variant="outlined"
          inputProps={{ type: 'number' }}
          label={formField.renderParameters.displayName}
          error={meta.dirty && meta.error}
          helperText={meta.dirty && meta.error ? meta.error : ''}
          required
          value={input.value.value}
          disabled={isReadOnly}
          onChange={(ev) => {
            const newInput: FormFieldSubmissionNumber = {
              id: formField.id,
              type: FieldType.NUMBER,
              value: Number.parseInt(ev.target.value)
            };
            input.onChange(newInput);
          }}
        />
      )}
    </FormField>
  );
};

const EmailFormField = ({
  formField,
  isReadOnly
}: {
  formField: EmailFieldDenormalized;
  isReadOnly: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FormField<FormFieldSubmissionEmail>
      validate={(input) =>
        input && isValidEmail(input.value)
          ? undefined
          : t('Please enter a valid email address')
      }
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => (
        <TextField
          fullWidth
          variant="outlined"
          label={formField.renderParameters.displayName}
          error={meta.dirty && meta.error}
          helperText={meta.dirty && meta.error ? meta.error : ''}
          required
          value={input.value.value}
          disabled={isReadOnly}
          onChange={(ev) => {
            const newInput: FormFieldSubmissionEmail = {
              id: formField.id,
              type: FieldType.EMAIL,
              value: ev.target.value
            };
            input.onChange(newInput);
          }}
        />
      )}
    </FormField>
  );
};

const isAddressComplete = (address: Address, requireName: boolean) => {
  const completeWithoutName = !!(
    address.addressLine1 &&
    address.city &&
    address.country &&
    address.postalCode &&
    address.region
  );
  return completeWithoutName && (!requireName || address.name);
};

const AddressContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(4),
  marginBottom: p.theme.spacing(4)
}));

const AdressFormField = ({
  formField,
  isReadOnly
}: {
  formField: AdressFieldDenormalized;
  isReadOnly: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const name: 'name'[] = ['name'];
  const excludedFields = formField.validationParameters.nameRequired
    ? undefined
    : name;

  return (
    <FormField<FormFieldSubmissionAddress>
      validate={(input) => {
        return input &&
          isAddressComplete(
            input.value,
            formField.validationParameters.nameRequired
          )
          ? undefined
          : t('Please provide a complete address');
      }}
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => (
        <AddressContainer>
          <AddressForm
            variant="compact"
            value={input.value.value || EMPTY_ADDRESS()}
            disabled={isReadOnly}
            required={true}
            onChange={(nextAddress) => {
              const newInput: FormFieldSubmissionAddress = {
                id: formField.id,
                type: FieldType.ADDRESS,
                value: nextAddress
              };
              input.onChange(newInput);
            }}
            excludedFields={excludedFields}
            autoFocus={false}
          />
        </AddressContainer>
      )}
    </FormField>
  );
};

const UserRelationshipFormField = ({
  formField,
  isReadOnly
}: {
  formField: UserRelationFieldDenormalized;
  isReadOnly: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FormField<FormFieldSubmissionEmail>
      validate={(input) =>
        input && isValidEmail(input.value)
          ? undefined
          : t("Please enter this person's work email address")
      }
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => (
        <TextField
          fullWidth
          variant="outlined"
          label={formField.renderParameters.displayName}
          error={meta.dirty && meta.error}
          helperText={meta.dirty && meta.error ? meta.error : ''}
          required
          value={input.value.value}
          disabled={isReadOnly}
          onChange={(ev) => {
            const newInput: FormFieldSubmissionUserRelationship = {
              id: formField.id,
              type: FieldType.USER_RELATIONSHIP,
              value: ev.target.value
            };
            input.onChange(newInput);
          }}
        />
      )}
    </FormField>
  );
};

const DateFormField = ({
  formField,
  dateFormat,
  isReadOnly
}: {
  formField: DateFieldDenormalized;
  dateFormat: string;
  isReadOnly: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  // Display format for UI - full month name, day, year
  const displayFormat = 'MMMM DD, YYYY';
  // Storage format - org's date format (e.g., MM/DD/YYYY)
  const storageFormat = dateFormat;

  const validationIntervalFrom = formField.validationParameters.from
    ? toMomentFromAny(formField.validationParameters.from).startOf('d')
    : null;
  const validationIntervalTo = formField.validationParameters.to
    ? toMomentFromAny(formField.validationParameters.to).endOf('d')
    : null;

  const validate = (input: FormFieldSubmissionDate | undefined) => {
    if (formField.validationParameters.restrictToInterval) {
      const newValue = input?.value;
      if (!newValue) {
        return;
      }
      const dateValue = moment(newValue, storageFormat).startOf('d');
      if (validationIntervalFrom) {
        const isValidMin = validationIntervalFrom.isSameOrBefore(
          dateValue.valueOf()
        );
        if (!isValidMin) {
          return `${t(
            'Date must be on or after '
          )} ${validationIntervalFrom.format(displayFormat)}`;
        }
      }

      if (validationIntervalTo) {
        const isValidMin = validationIntervalTo.isSameOrAfter(dateValue);
        if (!isValidMin) {
          return `${t(
            'Date must be on or before '
          )} ${validationIntervalTo.format(displayFormat)}`;
        }
      }
    }
  };

  return (
    <FormField<FormFieldSubmissionDate>
      validate={validate}
      name={simpleHash(formField.id)}
    >
      {({ input, meta }) => {
        const hasValue = !!(input.value?.value);
        return (
          <div style={{ position: 'relative' }}>
            <KeyboardDatePicker
              fullWidth
              autoOk
              format={displayFormat}
              inputVariant="outlined"
              variant="inline"
              label={formField.renderParameters.displayName}
              error={meta.dirty && meta.error}
              helperText={meta.dirty && meta.error ? meta.error : ''}
              required
              placeholder={displayFormat}
              value={
                input.value?.value ? moment(input.value.value, storageFormat) : null
              }
              disabled={isReadOnly}
              onChange={(date) => {
                if (date === null) {
                  input.onChange(undefined);
                } else if (date && date.isValid()) {
                  const newInput: FormFieldSubmissionDate = {
                    id: formField.id,
                    type: FieldType.DATE,
                    value: date.format(storageFormat)
                  };
                  input.onChange(newInput);
                } else {
                  input.onChange(undefined);
                }
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date'
              }}
              InputProps={{
                onKeyDown: (e) => {
                  // Prevent all keyboard input except Tab (for accessibility)
                  if (e.key !== 'Tab') {
                    e.preventDefault();
                  }
                },
                onClick: (e) => {
                  const pickerButton = e.currentTarget.parentElement?.querySelector(
                    '[aria-label="change date"]'
                  ) as HTMLButtonElement;
                  if (pickerButton && !isReadOnly) {
                    pickerButton.click();
                  }
                }
              }}
            />
            {hasValue && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  input.onChange(undefined);
                }}
                disabled={isReadOnly}
                aria-label="clear date"
                style={{
                  position: 'absolute',
                  right: 48,
                  top: 4,
                  padding: 4
                }}
              >
                <X size={24} />
              </IconButton>
            )}
          </div>
        );
      }}
    </FormField>
  );
};

export const EnrollmentFormField = ({
  formField,
  dateFormat,
  isReadOnly
}: {
  formField: EnrollmentFormFieldDenormalized;
  dateFormat: string;
  isReadOnly: boolean;
}): JSX.Element => {
  switch (formField.type) {
    case FieldType.TEXT:
      return <TextFormField formField={formField} isReadOnly={isReadOnly} />;
    case FieldType.EMAIL:
      return <EmailFormField formField={formField} isReadOnly={isReadOnly} />;
    case FieldType.DATE:
      return (
        <DateFormField
          formField={formField}
          dateFormat={dateFormat}
          isReadOnly={isReadOnly}
        />
      );
    case FieldType.USER_RELATIONSHIP:
      return (
        <UserRelationshipFormField
          formField={formField}
          isReadOnly={isReadOnly}
        />
      );
    case FieldType.ADDRESS:
      return <AdressFormField formField={formField} isReadOnly={isReadOnly} />;
    case FieldType.NUMBER:
      return <NumberFormField formField={formField} isReadOnly={isReadOnly} />;
  }
};

const BlockContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const FormContainerOuter = styled('div')<{ noBorder?: boolean }>((p) => ({
  border: p.noBorder ? 'none' : p.theme.custom.border.standard
}));

const FormContainer = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  backgroundColor: 'white'
}));

const IntroContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2)
}));
const LogoContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2)
}));

const SuccessIllustrationImg = styled('img')((p) => ({
  width: '50%'
}));

const SuccessLogoImg = styled('img')((p) => ({
  height: p.theme.spacing(6)
}));

const CenteredContent = styled('div')((p) => ({
  textAlign: 'center'
}));

const PreviewHintContainerWithMargin = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

export const PreviewHint = ({ active }: { active: boolean }) => {
  const { t } = useUnsafeTranslation();
  if (!active) {
    return null;
  }
  return (
    <InfoBox color="yellow">
      {t(
        'This is a preview. You can interact with it, but no submissions will be created.'
      )}
    </InfoBox>
  );
};

export const ReadOnlyHint = ({ active }: { active: boolean }) => {
  const { t } = useUnsafeTranslation();
  if (!active) {
    return null;
  }
  return (
    <InfoBox color="yellow">
      {t('This form is in read only mode and cannot be updated.')}
    </InfoBox>
  );
};

const SubmissionSuccessForTryOutOrg = ({
  enrollmentForm,
  enrolleeInfo,
  onBack,
  preview,
  data
}: {
  enrollmentForm: EnrollmentFormDenormalized;
  enrolleeInfo: EnrolleeInfo;
  onBack: () => void;
  preview?: boolean;
  data: {
    location: string;
    department: string;
  };
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <PreviewHintContainerWithMargin>
        <PreviewHint active={!!preview} />
      </PreviewHintContainerWithMargin>
      <CenteredContent>
        <ImageContainer align="center">
          <SuccessLogoImg src={enrollmentForm.org.logoUrl} />
        </ImageContainer>

        <ImageContainer align="center">
          <SuccessIllustrationImg src="/images/enrollmentForms/submission-successful.svg" />
        </ImageContainer>

        <Typography component="h1" variant="h1">
          Submission successful!
        </Typography>
        <br></br>
        <Typography component="p" variant="subtitle1">
          <b>
            {enrolleeInfo.fullName} ({enrolleeInfo.email}){' '}
          </b>
          will be enrolled in <b>{enrollmentForm.experienceTitle}</b>.
        </Typography>
        <br></br>
        <Typography component="p" variant="subtitle1">
          You'll receive 1 email per day over the next 3 days. You've selected{' '}
          <b>{data.location}</b> location and <b>{data.department}</b>{' '}
          department, so your experience will be personalized based on this.
        </Typography>
        <br></br>
        <Typography component="p" variant="subtitle1">
          Questions? Reach out to{' '}
          <MailTo
            color="primary"
            email={enrollmentForm.mailContact.email}
            subject={`[Enrollment Form - ${enrollmentForm.experienceTitle}]`}
          >
            {enrollmentForm.mailContact.name ||
              enrollmentForm.mailContact.email}
          </MailTo>
        </Typography>

        <br></br>
        <Button variant="contained" color="primary" onClick={() => onBack()}>
          {t('Enroll someone else')}
        </Button>
      </CenteredContent>
    </>
  );
};

const SubmissionSuccess = ({
  enrollmentForm,
  enrolleeInfo,
  onBack,
  preview
}: {
  enrollmentForm: EnrollmentFormDenormalized;
  enrolleeInfo: EnrolleeInfo;
  onBack: () => void;
  preview?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <PreviewHintContainerWithMargin>
        <PreviewHint active={!!preview} />
      </PreviewHintContainerWithMargin>
      <CenteredContent>
        <ImageContainer align="center">
          <SuccessLogoImg src={enrollmentForm.org.logoUrl} />
        </ImageContainer>

        <ImageContainer align="center">
          <SuccessIllustrationImg src="/images/enrollmentForms/submission-successful.svg" />
        </ImageContainer>

        <Typography component="h1" variant="h1">
          Submission successful!
        </Typography>
        <br></br>
        <Typography component="p" variant="subtitle1">
          <b>
            {enrolleeInfo.fullName} ({enrolleeInfo.email}){' '}
          </b>
          will be enrolled in <b>{enrollmentForm.experienceTitle}</b> according
          to the defined process. Please note this may not happen instantly.
        </Typography>
        <br></br>
        <Typography component="p" variant="subtitle1">
          Questions? Reach out to{' '}
          <MailTo
            color="primary"
            email={enrollmentForm.mailContact.email}
            subject={`[Enrollment Form - ${enrollmentForm.experienceTitle}]`}
          >
            {enrollmentForm.mailContact.name ||
              enrollmentForm.mailContact.email}
          </MailTo>
        </Typography>

        <br></br>
        <Button variant="contained" color="primary" onClick={() => onBack()}>
          {t('Enroll someone else')}
        </Button>
      </CenteredContent>
    </>
  );
};

const SubmitButtonContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'row',
  gap: p.theme.spacing(2)
}));
const PageBodyWrapper = styled('div')<{
  noTopPadding?: boolean;
  noSidePadding?: boolean;
}>`
  flex: 1;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  padding-top: 0;
  padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(2))}px;
  padding-bottom: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
  padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(2))}px;

  @media (min-width: 600px) {
    padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(4))}px;
    padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(4))}px;
  }

  @media (min-width: 1600px) {
    padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
    padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
  }

  background-color: ${(p) => p.theme.palette.grey[100]};
`;

const EnrollmentFormPageContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(6)
}));

const EnrollmentFormPageWrapper: React.FC = ({ children }) => (
  <PageBodyWrapper>
    <Page width="M">
      <EnrollmentFormPageContainer>{children}</EnrollmentFormPageContainer>
    </Page>
  </PageBodyWrapper>
);

type FormValues = {
  [fieldId: string]: FormFieldSubmission;
};

export const EnrollmentForm = forwardRef<
  {
    getFormMeasurements: () => DOMRect | null;
  },
  {
    enrollmentForm: EnrollmentFormDenormalized;
    onSubmit: (
      submissionData: FormFieldSubmission[]
    ) => Promise<SubmitFormResponse>;
    onCancel?: () => void;
    preview?: boolean;
    initialFormSubmission?: Doc<IEnrollmentFormSubmission>;
    adminMode?: boolean;
    isReadOnly?: boolean;
    skipSuccessScreen?: boolean;
    noBorder?: boolean;

    submitButtonLabel?: string;
  }
>(
  (
    {
      enrollmentForm,
      onSubmit,
      onCancel,
      preview,
      initialFormSubmission,
      adminMode,
      isReadOnly,
      noBorder,
      skipSuccessScreen,
      submitButtonLabel
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useUnsafeTranslation();
    useStandaloneAppThemeOverride(enrollmentForm.org.experienceTheming);
    const { enqueueSnackbarError } = useSnackbar();

    const initialValues: FormValues = initialFormSubmission
      ? keyBy(initialFormSubmission.data.data, (f) => simpleHash(f.id))
      : {};
    const [submitted, setSubmitted] = useState<{
      resp: SubmitFormResponse;
      values: FormValues;
    } | null>(null);
    const [enrolleeInfo, setEnrolleeInfo] = useState<EnrolleeInfo>({
      fullName: '',
      email: ''
    });

    useImperativeHandle(ref, () => {
      return {
        getFormMeasurements: () =>
          containerRef.current?.getBoundingClientRect() || null
      };
    });

    if (!!submitted) {
      if (
        submitted.resp.status === SubmissionResponseStatus.SUCCESS &&
        submitted.resp.isTryOutDadoOrg
      ) {
        return (
          <SubmissionSuccessForTryOutOrg
            enrolleeInfo={enrolleeInfo}
            enrollmentForm={enrollmentForm}
            onBack={() => setSubmitted(null)}
            preview={preview}
            data={{
              department:
                (Object.values(submitted?.values || {}).find(
                  (f) => f.id === 'department'
                )?.value as string) || '',
              location:
                (Object.values(submitted?.values || {}).find(
                  (f) => f.id === 'location'
                )?.value as string) || ''
            }}
          />
        );
      }
      return (
        <SubmissionSuccess
          enrolleeInfo={enrolleeInfo}
          enrollmentForm={enrollmentForm}
          onBack={() => setSubmitted(null)}
          preview={preview}
        />
      );
    }

    const finalSubmitButtonLabel = submitButtonLabel || t('Submit');

    return (
      <FormContainerOuter noBorder={noBorder}>
        <PreviewHint active={!!preview} />
        <ReadOnlyHint active={!!isReadOnly} />
        <FormContainer ref={containerRef}>
          <FlexContainer
            direction="column"
            fullWidth
            alignItems="flex-start"
            marginBottom={2}
          >
            <LogoContainer>
              <OrgLogo d={enrollmentForm.org} size={60} />
            </LogoContainer>

            <FlexItem flex={1}>
              <Typography component="h6" variant="h6" color="textSecondary">
                {t('Enrollment Form')}
              </Typography>
              <Typography component="h3" variant="h3">
                {enrollmentForm.experienceTitle}
              </Typography>
            </FlexItem>

            <IntroContainer>
              <MarkdownText t={enrollmentForm.introduction} />
            </IntroContainer>
          </FlexContainer>
          <Form<FormValues>
            initialValues={initialValues}
            onSubmit={async (values) => {
              try {
                const response = await onSubmit(Object.values(values));
                if (response.status === SubmissionResponseStatus.SUCCESS) {
                  console.log(response, values);
                  setEnrolleeInfo(response.enrolleeInfo);
                  if (!skipSuccessScreen) {
                    setSubmitted({
                      resp: response,
                      values
                    });
                  }
                } else if (response.status === SubmissionResponseStatus.ERROR) {
                  console.error(response.errorMessage);

                  enqueueSnackbarError(
                    `Your form submission was unsuccessful. Please try again later or contact ${enrollmentForm.mailContact.email}`
                  );
                } else {
                  //TODO Handle SSO  ISSUE responses
                }
              } catch (e) {
                console.error(e);
                enqueueSnackbarError(
                  `Your form submission was unsuccessful. Please try again later or contact ${enrollmentForm.mailContact.email}`
                );
              }
            }}
          >
            {({ submitting, pristine, values, errors }) => {
              const disabled =
                (errors instanceof Object &&
                  Object.values(errors).length > 0) ||
                (errors instanceof Array && errors.length > 0) ||
                pristine ||
                !!isReadOnly;

              return (
                <>
                  {enrollmentForm.fields.map((f) => (
                    <BlockContainer>
                      <EnrollmentFormField
                        key={f.id}
                        formField={f}
                        dateFormat={enrollmentForm.org.dateFormats?.MMDDYYYY || 'MM/DD/YYYY'}
                        isReadOnly={!!isReadOnly}
                      />
                    </BlockContainer>
                  ))}
                  <SubmitButtonContainer>
                    {onCancel && (
                      <Button onClick={onCancel}>{t('Cancel')}</Button>
                    )}
                    <FormSubmitButton
                      variant={'contained'}
                      color={'primary'}
                      disabled={disabled}
                      submitting={submitting}
                      submitComponent={
                        <>
                          <ButtonLoader /> {finalSubmitButtonLabel}
                        </>
                      }
                    >
                      {finalSubmitButtonLabel}
                    </FormSubmitButton>
                  </SubmitButtonContainer>

                  {/* leaving this here since it can be useful for debugging */}
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre>
                <pre>{JSON.stringify(enrollmentForm, null, 2)}</pre> */}
                </>
              );
            }}
          </Form>
        </FormContainer>
      </FormContainerOuter>
    );
  }
);

export const EnrollmentFormPage = ({
  accessToken
}: {
  accessToken: string;
}) => {
  const [
    enrollmentFormRenderInfo,
    loading,
    error
  ] = useEnrollmentFormRenderInfo(accessToken);
  const { t } = useUnsafeTranslation();
  const { theming } = useStandaloneAppContext();
  useErrorLogger('standaloneEnrollmentForm get', error);

  useEffect(() => {
    if (enrollmentFormRenderInfo) {
      theming.setOverrides(enrollmentFormRenderInfo.org.experienceTheming);
    }
  }, [theming, enrollmentFormRenderInfo]);

  if (loading) {
    return <Loader />;
  }
  if (error?.status === 'SSO_WRONG_USER_IN_SESSION') {
    return (
      <WrongSsoUserInSession
        email={error.sessionEmail}
        provider={error.ssoProvider}
      />
    );
  }

  if (NOT_FOUND_MESSAGES.has(error?.message)) {
    return <NotFound />;
  }

  if (!enrollmentFormRenderInfo) {
    return (
      <FlexContainer direction="column">
        <SomethingWentWrong height={50} />
        <Typography>
          {t(
            'This form is no longer active. Please check the place where you found this link, to see if an updated form is available.'
          )}
        </Typography>
      </FlexContainer>
    );
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <MaybeImpersonationBanner user={enrollmentFormRenderInfo.user} />
      <EnrollmentFormPageWrapper>
        <EnrollmentForm
          enrollmentForm={enrollmentFormRenderInfo}
          onSubmit={async (values) => {
            const enrolleeInfo = await submitEnrollmentForm({
              accessToke: enrollmentFormRenderInfo.accessToken,
              data: values
            });
            return enrolleeInfo;
          }}
        />
      </EnrollmentFormPageWrapper>
    </MuiPickersUtilsProvider>
  );
};
